import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MessageCircle, UserPlus, Settings2, Lock, Check, X, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { ChatProfileSetup } from "@/components/chat/ChatProfileSetup";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage } from "@/lib/api";
import { chat } from "@/lib/chat/engine";
import { chatDb } from "@/lib/chat/db";

export function useChatEngine(userId, enabled = true) {
  const [status, setStatus] = useState(chat.ready ? "ready" : "starting");
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!enabled || !userId) return;
    let alive = true;
    chat.start(userId).then(() => alive && setStatus("ready")).catch((e) => { if (!alive) return; setStatus("error"); setError(e); });
    return () => { alive = false; };
  }, [userId, enabled]);
  return { status, error };
}

export default function Chat() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const verified = user?.email_verified === true || user?.auth_mode === "username";
  const [me, setMe] = useState(undefined);
  const [convs, setConvs] = useState([]);
  const [reqs, setReqs] = useState({ incoming: [], outgoing: [] });
  const [last, setLast] = useState({});
  const [unread, setUnread] = useState({});
  const { status, error } = useChatEngine(user?.id, verified && !!me?.profile);

  const load = useCallback(async () => {
    try {
      const [m, c, r] = await Promise.all([api.get("/chat/me"), api.get("/chat/conversations").catch(() => ({ data: [] })), api.get("/chat/requests").catch(() => ({ data: { incoming: [], outgoing: [] } }))]);
      setMe(m.data); setConvs(c.data); setReqs(r.data);
      setLast(await chatDb.lastByPeer()); setUnread(await chatDb.unreadCounts());
    } catch (e) { if (e?.response?.status !== 403) toast.error(errorMessage(e)); setMe(null); }
  }, []);
  useEffect(() => { if (verified) load(); }, [verified, load]);
  useEffect(() => {
    const refresh = () => load();
    chat.addEventListener("message", refresh); chat.addEventListener("event", refresh); chat.addEventListener("removed", refresh); chat.addEventListener("expired", refresh);
    return () => { chat.removeEventListener("message", refresh); chat.removeEventListener("event", refresh); chat.removeEventListener("removed", refresh); chat.removeEventListener("expired", refresh); };
  }, [load]);

  const act = async (id, action) => { try { await api.post(`/chat/requests/${id}/${action}`); toast.success(action === "accept" ? "Conversa liberada." : "Solicitação recusada."); load(); } catch (e) { toast.error(errorMessage(e)); } };

  if (!verified) {
    return (
      <div className="ga-fade-up pb-10" data-testid="chat-page">
        <PageHeader title="Chat" subtitle="Conversas cifradas de ponta a ponta" />
        <div className="ga-card mx-5 p-5" data-testid="chat-requires-verified">
          <div className="flex items-center gap-2 font-semibold"><Lock size={18} /> Confirme seu e-mail</div>
          <p className="ga-muted mt-1 text-sm">O Chat é exclusivo para contas verificadas. Confirme seu e-mail em Segurança para continuar.</p>
          <Link to="/seguranca" className="ga-btn-primary mt-3 inline-flex w-full" data-testid="chat-go-security">Ir para Segurança</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="ga-fade-up pb-10" data-testid="chat-page">
      <PageHeader title="Chat" subtitle="Conversas cifradas de ponta a ponta" right={me?.profile && (
        <div className="flex gap-2">
          <button onClick={() => navigate("/chat/novo")} className="ga-surface flex h-10 w-10 items-center justify-center rounded-full" aria-label="Novo contato" data-testid="chat-new-button"><UserPlus size={18} /></button>
          <button onClick={() => navigate("/chat/ajustes")} className="ga-surface flex h-10 w-10 items-center justify-center rounded-full" aria-label="Ajustes do Chat" data-testid="chat-settings-button"><Settings2 size={18} /></button>
        </div>)} />
      <div className="space-y-4 px-5">
        {me === undefined && <div className="ga-muted text-sm">Carregando...</div>}
        {me !== undefined && !me?.profile && <ChatProfileSetup onDone={load} />}
        {me?.profile && (
          <>
            <div className="flex items-center gap-2 rounded-2xl px-3 py-2 text-xs" style={{ background: "var(--ga-surface)" }} data-testid="chat-e2ee-banner">
              <ShieldCheck size={14} style={{ color: "#13C9A3" }} /><span className="ga-muted">Você é <b>@{me.profile.username}</b>. {status === "ready" ? "Chaves ativas neste aparelho." : status === "error" ? (error?.response ? errorMessage(error) : (error?.message || "Falha ao iniciar a criptografia.")) : "Preparando chaves..."}</span>
            </div>
            {reqs.incoming.length > 0 && (
              <section className="ga-card p-4" data-testid="chat-requests">
                <h2 className="text-sm font-semibold">Solicitações ({reqs.incoming.length})</h2>
                <ul className="mt-2 space-y-2">{reqs.incoming.map((r) => (
                  <li key={r.id} className="flex items-center gap-2 text-sm" data-testid={`chat-request-${r.user.username}`}>
                    <div className="min-w-0 flex-1"><div className="truncate font-medium">{r.user.display_name}</div><div className="ga-muted text-xs">@{r.user.username}</div></div>
                    <button onClick={() => act(r.id, "accept")} className="flex h-9 w-9 items-center justify-center rounded-full text-white" style={{ background: "#13C9A3" }} aria-label="Aceitar" data-testid={`chat-request-accept-${r.user.username}`}><Check size={16} /></button>
                    <button onClick={() => act(r.id, "decline")} className="ga-surface flex h-9 w-9 items-center justify-center rounded-full text-red-500" aria-label="Recusar" data-testid={`chat-request-decline-${r.user.username}`}><X size={16} /></button>
                  </li>))}</ul>
              </section>
            )}
            <section className="space-y-2" data-testid="chat-conversations">
              {convs.map((c) => {
                const l = last[c.peer.user_id]; const n = unread[c.peer.user_id] || 0;
                return (
                  <button key={c.id} onClick={() => navigate(`/chat/c/${c.peer.user_id}`)} className="ga-card flex w-full items-center gap-3 p-4 text-left transition-transform active:scale-[0.99]" data-testid={`chat-conv-${c.peer.username}`}>
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-base font-bold text-white" style={{ background: "#4E78FF" }}>{c.peer.display_name[0]?.toUpperCase()}</div>
                    <div className="min-w-0 flex-1"><div className="flex items-center gap-2"><span className="truncate font-semibold">{c.peer.display_name}</span><span className="ga-muted text-xs">@{c.peer.username}</span></div>
                      <div className="ga-muted truncate text-xs">{c.blocked ? "Bloqueado" : l ? (l.once ? "Mensagem de visualização única" : l.kind === "file" ? `${l.mine ? "Você: " : ""}Anexo · ${l.att?.name || ""}` : `${l.mine ? "Você: " : ""}${l.text}`) : "Conversa liberada. Diga olá!"}</div></div>
                    {n > 0 && <span className="rounded-full px-2 py-0.5 text-xs font-bold text-white" style={{ background: "#13C9A3" }} data-testid={`chat-unread-${c.peer.username}`}>{n}</span>}
                  </button>);
              })}
              {!convs.length && (
                <div className="ga-card p-6 text-center" data-testid="chat-empty">
                  <MessageCircle size={36} className="mx-auto" style={{ color: "#13C9A3" }} />
                  <p className="mt-2 text-sm font-semibold">Nenhuma conversa ainda</p>
                  <p className="ga-muted mt-1 text-xs">Adicione alguém pelo @nome, código ou QR Code. A conversa só começa quando a outra pessoa aceitar.</p>
                  <button onClick={() => navigate("/chat/novo")} className="ga-btn-primary mt-4 w-full" data-testid="chat-empty-new">Adicionar contato</button>
                </div>
              )}
              {reqs.outgoing.length > 0 && <p className="ga-muted px-1 text-xs" data-testid="chat-outgoing">Aguardando aceite: {reqs.outgoing.map((r) => `@${r.user.username}`).join(", ")}</p>}
            </section>
          </>
        )}
      </div>
    </div>
  );
}
